import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { is } from 'typia'
import type { StripeElementLocale } from '@stripe/stripe-js'

type AddressDTO = {
  city: string | null
  street: string | null
  zipcode: string | null
  country_code: string | null
  // Optional: a cs-payment deploy older than ORD-2482 never sends these keys at all.
  // Must stay optional so components can deploy ahead of that backend change.
  state?: string | null
  line_2?: string | null
}

type StripeBillingDetailsDTO = {
  phone_number: string | null
  address: AddressDTO | null
  // Optional for the same reason: absent on responses from a pre-ORD-2482 backend.
  enforce_never_collect?: boolean
}

type StripePaymentIntentDTO = {
  public_api_key: string
  client_secret: string
  locale: StripeElementLocale
  return_url: string
  payment_methods: string[]
  render_express_checkout: boolean
  full_user_name: string
  billing_details: StripeBillingDetailsDTO
}

type StripeAddress = {
  city: string | null
  street: string | null
  zipcode: string | null
  countryCode: string | null
  state: string | null
  line2: string | null
}

export type StripeBillingDetails = {
  phoneNumber: string | null
  address: StripeAddress | null
  enforceNeverCollect: boolean
}

export type StripePaymentIntent = {
  publicApiKey: string
  clientSecret: string
  locale: StripeElementLocale
  returnUrl: string
  paymentMethods: string[]
  renderExpressCheckout: boolean
  fullUserName: string
  billingDetails: StripeBillingDetails
}


export class FetchStripePaymentIntentRequest {
  url: URL
  method = 'POST' as const
  headers = { 'Content-Type': 'application/json' }

  constructor(path: string) {
    this.url = new URL(path, window.location.origin)
  }
}

export type CloudSuiteStripeBackend = {
  fetchPaymentIntent: (request: FetchStripePaymentIntentRequest) => Promise<StripePaymentIntent>
}

export const CLOUD_SUITE_STRIPE_BACKEND = Symbol('CLOUD_SUITE_STRIPE_BACKEND')

@registerDependency(FAKE, CLOUD_SUITE_STRIPE_BACKEND)
export class FakeCloudSuiteStripeBackend implements CloudSuiteStripeBackend {
  requests: FetchStripePaymentIntentRequest[] = []
  response: StripePaymentIntentDTO = {
    public_api_key: 'super public',
    client_secret: 'super secret',
    locale: 'hu',
    return_url: 'mooie-urlrl',
    payment_methods: [],
    render_express_checkout: false,
    full_user_name: 'Barrie bats Ball',
    billing_details: {
      address: {
        city: 'Houten',
        street: 'Elzenkade',
        zipcode: '1000AA',
        country_code: 'NL',
        state: null,
        line_2: null,
      },
      phone_number: '0611111111',
      enforce_never_collect: false,
    }
  }

  async fetchPaymentIntent(request: FetchStripePaymentIntentRequest) {
    this.requests.push(request)
    return parseStripePaymentIntent(this.response)
  }
}

@registerDependency(REAL, CLOUD_SUITE_STRIPE_BACKEND)
export class RealCloudSuiteStripeBackend implements CloudSuiteStripeBackend {
  async fetchPaymentIntent(request: FetchStripePaymentIntentRequest) {
    const response = await fetch(request.url, {
      method: request.method,
      headers: request.headers,
      body: JSON.stringify({})
    })
    return parseStripePaymentIntent(await response.json())
  }
}

const parseStripePaymentIntent = (data: unknown): StripePaymentIntent => {
  if (is<StripePaymentIntentDTO>(data)) {
    const billing_details = {
      address: {
        city: data.billing_details.address?.city ?? null,
        street: data.billing_details.address?.street ?? null,
        zipcode: data.billing_details.address?.zipcode ?? null,
        countryCode: data.billing_details.address?.country_code ?? null,
        state: data.billing_details.address?.state ?? null,
        line2: data.billing_details.address?.line_2 ?? null,
      },
      phoneNumber: data.billing_details.phone_number,
      enforceNeverCollect: data.billing_details.enforce_never_collect ?? false,
    }
    return {
      publicApiKey: data.public_api_key,
      clientSecret: data.client_secret,
      locale: data.locale,
      returnUrl: data.return_url,
      paymentMethods: data.payment_methods,
      renderExpressCheckout: data.render_express_checkout,
      fullUserName: data.full_user_name,
      billingDetails: billing_details,
    }
  }

  throw new Error('Response is 🥦 but should be StripePaymentIntentDTO')
}
